import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Box, Typography, useTheme } from "@mui/material";
import { getShortMonthName } from "../../utils/BusinessPlanningCalculator";
import { IBusinessPlanningMonthlyTrend } from "../../types/types";
import { formatCurrency } from "../../utils/common";

interface BusinessPlanningTrendChartProps {
  projectedTrend: { month: number; projectedGci: number }[];
  currentYearTrend?: IBusinessPlanningMonthlyTrend[];
  underContractGci?: number;
  underContractLastMonth?: number;
  currentGciWithUnderContract?: number;
  forecastYear: number;
  compact?: boolean;
}

export const BusinessPlanningTrendChart: React.FC<
  BusinessPlanningTrendChartProps
> = ({
  projectedTrend,
  currentYearTrend,
  underContractGci,
  underContractLastMonth,
  currentGciWithUnderContract,
  forecastYear,
  compact = false,
}) => {
  const theme = useTheme();

  // Current month for reference line (only show if forecasting this year)
  const currentMonth = new Date().getMonth() + 1;

  // Combine projected and current year data
  const chartData = projectedTrend.map((proj) => {
    const currentYearItem = currentYearTrend?.find(
      (c) => c.month === proj.month,
    );

    // For current year data: use actual value if exists, 0 for past/current months with no data, null for future months
    let currentGciValue = null;
    if (currentYearTrend && currentYearTrend.length > 0) {
      if (currentYearItem) {
        // Month has data - use it
        currentGciValue = currentYearItem.cumulativeGci;
      } else if (proj.month <= currentMonth) {
        // Past or current month with no data - show 0
        currentGciValue = 0;
      }
      // Future months remain null (no line drawn)
    }

    return {
      month: proj.month,
      monthName: getShortMonthName(proj.month),
      projectedGci: proj.projectedGci,
      currentGci: currentGciValue,
      // Add the Under Contract point at the month of last under contract transaction
      currentGciWithUnderContractValue:
        underContractLastMonth && proj.month === underContractLastMonth
          ? currentGciWithUnderContract
          : null,
    };
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: "white",
            padding: 1.5,
            border: "1px solid #ccc",
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: "100%", height: compact ? 300 : 400 }}>
      <ResponsiveContainer>
        <LineChart
          data={chartData}
          margin={
            compact
              ? { top: 10, right: 10, left: 10, bottom: 10 }
              : { top: 20, right: 30, left: 20, bottom: 20 }
          }
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis
            dataKey="monthName"
            tick={{ fontSize: compact ? 10 : 12 }}
            tickLine={false}
            axisLine={{ stroke: "#ccc" }}
          />
          <YAxis
            tick={{ fontSize: compact ? 10 : 12 }}
            tickLine={false}
            axisLine={{ stroke: "#ccc" }}
            tickFormatter={(value) =>
              new Intl.NumberFormat("en-US", {
                notation: "compact",
                compactDisplay: "short",
                style: "currency",
                currency: "USD",
                maximumFractionDigits: 0,
              }).format(value)
            }
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            wrapperStyle={{
              paddingBottom: compact ? 15 : 10,
              fontSize: compact ? "11px" : "14px",
            }}
            iconType="line"
            iconSize={compact ? 10 : 14}
          />

          {/* Projected GCI Line */}
          <Line
            type="monotone"
            dataKey="projectedGci"
            name={`Projected GCI (${forecastYear})`}
            stroke={theme.palette.primary.main}
            strokeWidth={compact ? 2 : 3}
            dot={{ fill: theme.palette.primary.main, r: compact ? 3 : 4 }}
            activeDot={{ r: compact ? 4 : 6 }}
          />

          {/* Current Year GCI Line (if applicable) */}
          {currentYearTrend && currentYearTrend.length > 0 && (
            <Line
              type="monotone"
              dataKey="currentGci"
              name="Current GCI (YTD)"
              stroke={theme.palette.success.main}
              strokeWidth={compact ? 2 : 3}
              // strokeDasharray="3 3"
              dot={{ fill: theme.palette.success.main, r: compact ? 3 : 4 }}
              activeDot={{ r: compact ? 4 : 6 }}
              connectNulls={true}
            />
          )}

          {/* Current GCI with Under Contract - Single Point at Last UC Transaction Month */}
          {currentYearTrend &&
            underContractGci !== undefined &&
            underContractGci !== null &&
            underContractLastMonth !== undefined &&
            underContractLastMonth !== null &&
            currentGciWithUnderContract !== undefined &&
            currentGciWithUnderContract !== null && (
              <Line
                type="monotone"
                dataKey="currentGciWithUnderContractValue"
                name="Current GCI + Under Contracts"
                stroke={theme.palette.warning.main}
                fill={theme.palette.warning.main}
                strokeWidth={compact ? 2 : 3}
                dot={{
                  fill: theme.palette.warning.main,
                  r: compact ? 3 : 4,
                  //strokeWidth: 2,
                  //stroke: "#fff",
                }}
                activeDot={{ r: compact ? 4 : 6 }}
                connectNulls={true}
              />
            )}

          {/* Reference line for current month when forecasting this year */}
          {currentYearTrend && currentMonth <= 12 && (
            <ReferenceLine
              x={getShortMonthName(currentMonth)}
              stroke={theme.palette.success.main}
              strokeDasharray="3 3"
              label={{
                value: "Today",
                position: "top",
                fill: theme.palette.success.main,
                fontSize: 13,
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Legend explanation */}
      {!compact && (
        <Box sx={{ mt: 3, px: 2 }}>
          <Typography variant="caption" color="text.secondary">
            The chart shows your projected cumulative GCI throughout the year
            based on historical monthly patterns.
            {currentYearTrend && (
              <>
                {" "}
                The dashed line shows your actual YTD progress for comparison.
              </>
            )}
            {currentYearTrend &&
              underContractGci !== undefined &&
              underContractGci !== null &&
              underContractLastMonth !== undefined &&
              underContractLastMonth !== null && (
                <>
                  {" "}
                  The orange point at{" "}
                  {getShortMonthName(underContractLastMonth)} shows your GCI
                  including Under Contract transactions (
                  {formatCurrency(underContractGci)} pending).
                </>
              )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
